import {
  ChangeEvent,
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  mainProfilePhotoImageInputContainer,
  mainProfilePhotoImageInputText,
  profilePhotosSecondaryCard,
  profilePhotosSelectedImage
} from '@/components/ProfileData/styles.ts';
import DeleteButton from '@/components/ProfileData/ProfilePhotos/components/DeleteButton.tsx';
import CircularProgressBar from '@/components/ProfileData/ProfilePhotos/components/CircularProgress.tsx';
import ProfilePhotosLoader from '@/components/ProfileData/ProfilePhotos/components/ProfilePhotosLoader.tsx';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import ProfileController from '@/api/profile/profile.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { ServerVideoV2, VideoKind } from '@/shared/type/userDataFields.ts';
import { VideoState } from '@/enums/apiEnums.ts';
import { helpers } from '@/helpers.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import {
  removePendingVideo,
  savePendingVideo
} from '@/utils/pendingVideoCache.ts';
import { CameraVideoShape } from '@/components/common/svg/shapes/CameraVideoShape.tsx';
import PhotoStateLabel from '@/components/ProfileData/ProfilePhotos/components/PhotoStateLabel.tsx';
import { thumbHashToImageUrl } from '@/utils/thumbhash.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import { useVideoDeleteConfirm } from '@/hooks/useVideoDeleteConfirm.ts';

type ProfileVideoProps = {
  isEdit?: boolean;
  isAnyPhotoUploading?: boolean;
  onVideoUploaded?: (hid: string, url: string) => void;
  onVideoDeleted?: () => void;
  onUploadingChange?: (uploading: boolean) => void;
};

const MAX_VIDEO_SIZE_MB = 100;
const MAX_VIDEO_DURATION_SEC = 15;
const MIN_VIDEO_RESOLUTION = 720;

const getVideoMetadata = (
  file: File
): Promise<{ duration: number; width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    video.src = URL.createObjectURL(file);
  });

const ProfileVideo: FC<ProfileVideoProps> = ({
  isEdit = false,
  isAnyPhotoUploading = false,
  onVideoUploaded,
  onVideoDeleted,
  onUploadingChange
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { aboutMe, setAboutMe } = useProfileStore();

  const storeVideo = aboutMe?.profile?.v2videos?.[0];
  const storeVideoUrl = helpers.getVideoUrl(storeVideo?.variants ?? []);
  const storePosterUrl = helpers.getVideoPoster(storeVideo?.variants ?? []);
  const storeThumbHash = helpers.getVideoThumbHash(storeVideo?.variants ?? []);
  const storeVideoHid = storeVideo?.hid ?? null;

  const thumbhashDataUrl = useMemo(() => {
    if (!storeThumbHash) return undefined;
    try {
      return thumbHashToImageUrl(storeThumbHash);
    } catch {
      return undefined;
    }
  }, [storeThumbHash]);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploadingState] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const videoUrl = blobUrl ?? storeVideoUrl;
  const videoHid = storeVideoHid;
  const canDelete =
    !isEdit ||
    (!storeVideo?.in_review && storeVideo?.state === VideoState.STATE_ACTIVE);

  // Reset isVideoLoaded when video source changes
  useEffect(() => {
    setIsVideoLoaded(false);
  }, [storeVideoUrl, blobUrl]);

  const setIsUploading = (value: boolean) => {
    setIsUploadingState(value);
    onUploadingChange?.(value);
  };

  const showError = (msg: string) => {
    helpers.toast({
      id: MessageTypesUI.TYPE_SYSTEM_ERROR,
      content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} msg={msg} />
    });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || isUploading) return;

    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      showError(t('profile.video.error.size', { max: MAX_VIDEO_SIZE_MB }));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    try {
      const metadata = await getVideoMetadata(file);

      if (metadata.duration > MAX_VIDEO_DURATION_SEC) {
        showError(
          t('profile.video.error.duration', { max: MAX_VIDEO_DURATION_SEC })
        );
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      const minSide = Math.min(metadata.width, metadata.height);
      if (minSide < MIN_VIDEO_RESOLUTION) {
        showError(
          t('profile.video.error.resolution', { min: MIN_VIDEO_RESOLUTION })
        );
        if (inputRef.current) inputRef.current.value = '';
        return;
      }
    } catch {
      // ignore metadata errors, proceed with upload
    }

    const previewUrl = URL.createObjectURL(file);
    setIsUploading(true);
    setBlobUrl(previewUrl);

    try {
      const mimeType = file.type || 'video/mp4';

      const { upload_url, video_hid } = await ProfileController.prepareVideo(
        mimeType,
        file.size
      );

      await ProfileController.uploadToS3(
        upload_url,
        file,
        mimeType,
        setProgress
      );

      ProfileController.completeVideo(video_hid, mimeType, file.size).catch(
        () => {}
      );

      setProgress(0);

      const newServerVideo: ServerVideoV2 = {
        hid: video_hid,
        in_review: true,
        state: VideoState.STATE_PENDING,
        variants: [
          { url: previewUrl, kind: VideoKind.KIND_VIDEO },
          { url: previewUrl, kind: VideoKind.KIND_POSTER }
        ]
      };

      // Read fresh state to avoid stale closure
      const currentAboutMe = useProfileStore.getState().aboutMe;
      if (currentAboutMe) {
        setAboutMe({
          ...currentAboutMe,
          profile: {
            ...currentAboutMe.profile,
            v2videos: [newServerVideo]
          }
        });
      }

      // Cache file in IndexedDB for page reload while server processes video
      await savePendingVideo(video_hid, file);

      onVideoUploaded?.(video_hid, previewUrl);
    } catch (e) {
      URL.revokeObjectURL(previewUrl);
      setBlobUrl(null);
      setProgress(0);
      catchErrors(e, true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = useCallback(async () => {
    if (!videoHid) {
      setBlobUrl(null);
      return;
    }

    try {
      setIsDeleting(true);

      await ProfileController.deleteVideo(videoHid);

      if (storeVideoUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(storeVideoUrl);
      }
      setBlobUrl(null);
      await removePendingVideo();
      if (inputRef.current) {
        inputRef.current.value = '';
      }

      const currentAboutMe = useProfileStore.getState().aboutMe;
      if (currentAboutMe) {
        setAboutMe({
          ...currentAboutMe,
          profile: {
            ...currentAboutMe.profile,
            v2videos: (currentAboutMe.profile.v2videos || []).filter(
              (v) => v.hid !== videoHid
            )
          }
        });
      }

      onVideoDeleted?.();
    } catch (e) {
      catchErrors(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, [videoHid, storeVideoUrl, setAboutMe, onVideoDeleted]);

  const confirmDelete = useVideoDeleteConfirm(handleDelete);

  const cardStyle = profilePhotosSecondaryCard(!!videoUrl);

  return (
    <div style={cardStyle}>
      {videoUrl ? (
        isDeleting ? (
          <ProfilePhotosLoader />
        ) : (
          <>
            {progress > 0 && (
              <CircularProgressBar progress={progress} size={50} />
            )}
            {!blobUrl &&
              (storeThumbHash || storePosterUrl) &&
              !isVideoLoaded && (
                <div style={posterOverlayStyle}>
                  <ImageWithLoader
                    src={storePosterUrl}
                    thumbhash={storeThumbHash}
                    forceLoading={!isVideoLoaded}
                    alt=""
                    style={profilePhotosSelectedImage}
                    customLoader={<ProfilePhotosLoader />}
                  />
                </div>
              )}
            <video
              src={videoUrl}
              poster={
                blobUrl ? undefined : (thumbhashDataUrl ?? storePosterUrl)
              }
              style={videoStyle}
              muted
              playsInline
              autoPlay
              loop
              onPlaying={() => setIsVideoLoaded(true)}
            />
            {!isUploading &&
              !isDeleting &&
              (canDelete ? (
                <DeleteButton
                  handleDelete={isEdit ? confirmDelete : handleDelete}
                />
              ) : (
                <PhotoStateLabel />
              ))}
          </>
        )
      ) : (
        <>
          <div style={mainProfilePhotoImageInputContainer}>
            <CameraVideoShape />
            <span style={mainProfilePhotoImageInputText}>
              {t('registration.add_video')}
            </span>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="video/*"
            disabled={isAnyPhotoUploading}
            onChange={handleFileChange}
            style={inputStyle}
          />
        </>
      )}
    </div>
  );
};

const videoStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '20px'
};

const posterOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  transition: 'opacity 0.3s ease-out'
};

const inputStyle: CSSProperties = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  opacity: 0,
  cursor: 'pointer'
};

export default ProfileVideo;
